/** API is not yet fully migrated to FQDN entity */
export interface SiteDocument {
  /** the document payload */
  document?: Record<string, any> | null;
}

export interface UpdateInternalDocumentsEvent
  extends UpdateInternalDocumentsEventOperationOneOf {
  /** insert/update documents */
  update?: InternalDocumentUpdateOperation;
  /** delete by document ids */
  deleteByIds?: DeleteByIdsOperation;
  /** delete documents matching filter */
  deleteByFilter?: DeleteByFilterOperation;
  /** update internal documents matching filter */
  updateByFilter?: InternalDocumentUpdateByFilterOperation;
  /** update only existing documents */
  updateExisting?: InternalUpdateExistingOperation;
  /** insert/update documents with versioning */
  versionedUpdate?: VersionedDocumentUpdateOperation;
  /** delete by document ids with versioning */
  versionedDeleteByIds?: VersionedDeleteByIdsOperation;
  /**
   * type of the documents
   * @minLength 2
   */
  documentType?: string;
  /**
   * language of the documents (mandatory)
   * @minLength 2
   */
  language?: string | null;
  /**
   * one or more search documents
   * @deprecated
   */
  addDocuments?: InternalDocument[];
  /**
   * one or more ids of indexed documents to be removed. Removal will happen before addition (if both provided)
   * @deprecated
   */
  removeDocumentIds?: string[];
  /** id to pass to processing notification */
  correlationId?: string | null;
  /** when event was created / issued */
  issuedAt?: Date | null;
}

/** @oneof */
export interface UpdateInternalDocumentsEventOperationOneOf {
  /** insert/update documents */
  update?: InternalDocumentUpdateOperation;
  /** delete by document ids */
  deleteByIds?: DeleteByIdsOperation;
  /** delete documents matching filter */
  deleteByFilter?: DeleteByFilterOperation;
  /** update internal documents matching filter */
  updateByFilter?: InternalDocumentUpdateByFilterOperation;
  /** update only existing documents */
  updateExisting?: InternalUpdateExistingOperation;
  /** insert/update documents with versioning */
  versionedUpdate?: VersionedDocumentUpdateOperation;
  /** delete by document ids with versioning */
  versionedDeleteByIds?: VersionedDeleteByIdsOperation;
}

export interface InternalDocument {
  /** document with mandatory fields (id) and with fields specific to the type of the document */
  document?: Record<string, any> | null;
}

export interface InternalDocumentUpdateOperation {
  /** documents to index or update */
  documents?: InternalDocument[];
}

export interface DeleteByIdsOperation {
  /** ids of the documents to delete */
  documentIds?: string[];
  /**
   * tenant id for custom tenancy strategy
   * @minLength 2
   * @maxLength 300
   */
  tenantId?: string | null;
}

export interface DeleteByFilterOperation {
  /** documents matching this filter wil be deleted. only filterable documents defined in document_type can be used for filtering */
  filter?: Record<string, any> | null;
  /**
   * tenant id for custom tenancy strategy
   * @minLength 2
   * @maxLength 300
   */
  tenantId?: string | null;
}

export interface InternalDocumentUpdateByFilterOperation {
  /** documents matching this filter will be updated */
  filter?: Record<string, any> | null;
  /** partial document to apply */
  document?: InternalDocument;
  /**
   * tenant id for custom tenancy strategy
   * @minLength 2
   * @maxLength 300
   */
  tenantId?: string | null;
}

export interface InternalUpdateExistingOperation {
  /** documents to update */
  documents?: InternalDocument[];
}

export interface VersionedDocumentUpdateOperation {
  /** documents to create or overwrite */
  documents?: InternalDocument[];
  /** versioning mode to use instead of default */
  versioningMode?: VersioningModeWithLiterals;
}

export enum VersioningMode {
  /** use default versioning mode agreed with search team */
  DEFAULT = 'DEFAULT',
  /** execute only if version is greater than existing */
  GREATER_THAN = 'GREATER_THAN',
  /** execute only if version is greater or equal to existing */
  GREATER_OR_EQUAL = 'GREATER_OR_EQUAL',
}

/** @enumType */
export type VersioningModeWithLiterals =
  | VersioningMode
  | 'DEFAULT'
  | 'GREATER_THAN'
  | 'GREATER_OR_EQUAL';

export interface VersionedDeleteByIdsOperation {
  /** ids with version of the documents to delete */
  documentIds?: VersionedDocumentId[];
  /**
   * tenant id for custom tenancy strategy
   * @minLength 2
   * @maxLength 300
   */
  tenantId?: string | null;
}

export interface VersionedDocumentId {
  /** document id */
  documentId?: string;
  /** document version */
  version?: string;
  /** versioning mode to use instead of default */
  versioningMode?: VersioningModeWithLiterals;
}

export interface UpdateDocumentsEvent
  extends UpdateDocumentsEventOperationOneOf {
  /** insert/update documents */
  update?: DocumentUpdateOperation;
  /** delete by document ids */
  deleteByIds?: V1DeleteByIdsOperation;
  /** delete documents matching filter */
  deleteByFilter?: V1DeleteByFilterOperation;
  /** update documents matching filter */
  updateByFilter?: UpdateByFilterOperation;
  /** update only existing documents */
  updateExisting?: UpdateExistingOperation;
  /**
   * application which owns documents
   * @minLength 2
   */
  appDefId?: string | null;
  /**
   * type of the documents
   * @minLength 2
   */
  documentType?: string | null;
  /**
   * language of the documents
   * @minLength 2
   */
  language?: string | null;
  /**
   * site documents belong to
   * @minLength 2
   */
  msId?: string | null;
}

/** @oneof */
export interface UpdateDocumentsEventOperationOneOf {
  /** insert/update documents */
  update?: DocumentUpdateOperation;
  /** delete by document ids */
  deleteByIds?: V1DeleteByIdsOperation;
  /** delete documents matching filter */
  deleteByFilter?: V1DeleteByFilterOperation;
  /** update documents matching filter */
  updateByFilter?: UpdateByFilterOperation;
  /** update only existing documents */
  updateExisting?: UpdateExistingOperation;
}

export interface DocumentUpdateOperation {
  /** documents to index or update */
  documents?: IndexDocument[];
}

export interface IndexDocument {
  /** data bag with non-searchable fields (url, image) */
  payload?: DocumentPayload;
  /** what type of users should documents be visible to */
  exposure?: EnumWithLiterals;
  /** document with mandatory fields (id, title, description) and with fields specific to the type of the document */
  document?: Record<string, any> | null;
  /** what member groups is the document exposed to. Used only with GROUP_PROTECTED exposure */
  permittedMemberGroups?: string[];
  /** if true SEO is disabled for this document */
  seoHidden?: boolean | null;
  /** if true the page is a lightbox popup */
  isPopup?: boolean | null;
}

export interface DocumentPayload {
  /** url of the page representing the document */
  url?: string | null;
  /** image which represents the document */
  documentImage?: DocumentImage;
}

export interface DocumentImage {
  /** the name of the image */
  name?: string;
  /** the width of the image */
  width?: number;
  /** the height of the image */
  height?: number;
}

export enum Enum {
  /** Default value. Means that permission not set */
  UNKNOWN = 'UNKNOWN',
  /** Protected exposure. Exposed to members and owners */
  PROTECTED = 'PROTECTED',
  /** Private exposure. Exposed to owners */
  PRIVATE = 'PRIVATE',
  /** Public exposure. Visible to everyone */
  PUBLIC = 'PUBLIC',
  /** Used for partial updates, to state that exposure is not changing */
  UNCHANGED = 'UNCHANGED',
  /** Protected to members of permitted groups and owners */
  GROUP_PROTECTED = 'GROUP_PROTECTED',
}

/** @enumType */
export type EnumWithLiterals =
  | Enum
  | 'UNKNOWN'
  | 'PROTECTED'
  | 'PRIVATE'
  | 'PUBLIC'
  | 'UNCHANGED'
  | 'GROUP_PROTECTED';

export interface V1DeleteByIdsOperation {
  /** ids of the documents to delete */
  documentIds?: string[];
}

export interface V1DeleteByFilterOperation {
  /** documents matching this filter wil be deleted. only filterable documents defined in document_type can be used for filtering */
  filter?: Record<string, any> | null;
}

export interface UpdateByFilterOperation {
  /** documents matching this filter will be updated */
  filter?: Record<string, any> | null;
  /** partial document to apply */
  document?: IndexDocument;
}

export interface UpdateExistingOperation {
  /** documents to update */
  documents?: IndexDocument[];
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface SearchRequest {
  /**
   * Text to search for. All searchable fields will be searched.
   * @maxLength 100
   */
  query?: string | null;
  /**
   * Document type of documents to search for. All document types are searched if not provided.
   * @minLength 2
   */
  documentType?: string | null;
  /** Fields to order by. */
  ordering?: OrderingClauses;
  /** Paging parameters. */
  paging?: SearchPaging;
  /**
   * Language to search in.
   * @minLength 2
   */
  language?: string | null;
  /** Filter in platformized query language (for example {'field': {'$eq': 'value'}}) */
  filter?: Record<string, any> | null;
  /** The facets to retrieve. */
  facets?: FacetClauses;
  /** Enable fuzzy search (eg. query 'kalvin clein' will match document with 'calvin klein' in title). */
  fuzzy?: boolean | null;
  /** Highlight texts matching the query. Highlighted text will be wrapped with <mark/> tag. Defaults to true if not provided. */
  highlight?: boolean | null;
  /** Searchable fields to search in. If not provided, search is executed on all searchable fields in schema */
  searchFields?: string[];
  /** A list of fields to include in the result set. If not provided, all fields of schema will be included. */
  fields?: string[];
  /**
   * The properties/overrides/experiments to enable for this request. Currently supported: `scoring_profile`, `result-format`.
   * @maxSize 10
   */
  properties?: SearchProperty[];
  /** Include seo hidden documents. Defaults to false if not provided. */
  includeSeoHidden?: boolean | null;
}

export interface OrderingClauses {
  ordering?: OrderingClause[];
}

export interface OrderingClause {
  /** @minLength 2 */
  fieldName?: string | null;
  direction?: DirectionWithLiterals;
}

export enum Direction {
  UninitializedDirection = 'UninitializedDirection',
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type DirectionWithLiterals =
  | Direction
  | 'UninitializedDirection'
  | 'ASC'
  | 'DESC';

export interface SearchPaging {
  /**
   * Number of items to skip in the result set.
   * @max 100000
   * @deprecated
   */
  skip?: number;
  /**
   * Number of items to fetch (effectively page size).
   * @max 1000
   */
  limit?: number;
  /**
   * Number of items to skip in the result set.
   * @max 100000
   */
  offset?: number;
}

export interface FacetClauses {
  /** Each entry represents a single facet with parameters. */
  clauses?: FacetClause[];
}

export interface FacetClause extends FacetClauseClauseOneOf {
  term?: TermFacet;
  aggregation?: AggregationFacet;
  hierarchical?: HierarchicalFacet;
}

/** @oneof */
export interface FacetClauseClauseOneOf {
  term?: TermFacet;
  aggregation?: AggregationFacet;
  hierarchical?: HierarchicalFacet;
}

export enum Aggregation {
  MIN = 'MIN',
  MAX = 'MAX',
  SUM = 'SUM',
}

/** @enumType */
export type AggregationWithLiterals = Aggregation | 'MIN' | 'MAX' | 'SUM';

export interface HierarchicalFacet extends HierarchicalFacetClauseOneOf {
  term?: TermFacet;
  aggregation?: AggregationFacet;
  nestedAggregation?: HierarchicalFacet;
}

/** @oneof */
export interface HierarchicalFacetClauseOneOf {
  term?: TermFacet;
  aggregation?: AggregationFacet;
}

export interface TermFacet {
  /** The name of the faceted attribute. */
  name?: string;
  /** Limit the number of facet values returned. Default is 10. */
  limit?: number | null;
}

export interface AggregationFacet {
  /** The name of the faceted attribute. */
  name?: string;
  /** Aggregation type. */
  aggregation?: AggregationWithLiterals;
}

export interface SearchProperty {
  name?: string;
  value?: any;
}

export interface SearchResponse {
  /** Documents matching filter and query. */
  documents?: Record<string, any>[] | null;
  nextPage?: NextPageResponse;
  /**
   * Facets provide "counts in categories" view. For example searching for "Nike" would return
   * (Shoes, 5), (Socks, 2) indicating numbers for matching by each faceted field.
   */
  facets?: FacetsResponse[];
}

export interface NextPageResponse {
  /** Total number of items across all pages */
  total?: number;
  /** The number of items to skip */
  skip?: number;
  /** The number of items to retrieve in one page */
  limit?: number;
}

export interface FacetsResponse extends FacetsResponseResponseOneOf {
  terms?: TermAggregationResponse;
  minAggregation?: MinAggregationResponse;
  maxAggregation?: MaxAggregationResponse;
  minMaxAggregation?: MinMaxAggregationResponse;
  hierarchicalAggregation?: HierarchicalAggregationResponse;
  sumAggregation?: SumAggregationResponse;
}

/** @oneof */
export interface FacetsResponseResponseOneOf {
  terms?: TermAggregationResponse;
  minAggregation?: MinAggregationResponse;
  maxAggregation?: MaxAggregationResponse;
  minMaxAggregation?: MinMaxAggregationResponse;
  hierarchicalAggregation?: HierarchicalAggregationResponse;
  sumAggregation?: SumAggregationResponse;
}

export interface FacetCountResponse {
  /** Facet field value (for example "Shoes", "Socks") */
  facetValue?: string;
  /** Document count within the group */
  count?: number;
}

export interface Value {
  value?: string;
  facets?: FacetsResponse;
  count?: number;
}

export interface TermAggregationResponse {
  /** Facet field (for example productCategory) */
  facet?: string;
  /** Facet values and document counts */
  facets?: FacetCountResponse[];
}

export interface MinAggregationResponse {
  /** Facet field (for example productPrice) */
  facet?: string;
  /** The minimum value across all documents */
  minValue?: number | null;
}

export interface MaxAggregationResponse {
  /** Facet field (for example productPrice) */
  facet?: string;
  /** The maximum value across all documents */
  maxValue?: number | null;
}

export interface MinMaxAggregationResponse {
  /** Facet field (for example productPrice) */
  facet?: string;
  /** The minimum value across all documents */
  minValue?: number | null;
  /** The maximum value across all documents */
  maxValue?: number | null;
}

export interface HierarchicalAggregationResponse {
  facet?: string;
  values?: Value[];
}

export interface SumAggregationResponse {
  /** Facet field (for example productPrice) */
  facet?: string;
  /** The sum value across all documents */
  value?: number | null;
}

export interface FederatedSearchRequest {
  /**
   * Query phrase to use.
   * @maxLength 100
   */
  query?: string | null;
  /**
   * Language to search in.
   * @minLength 2
   */
  language?: string | null;
  /**
   * Limit of documents to return per document type.
   * @min 1
   * @max 100
   */
  limit?: number | null;
  /** Enable fuzzy search (for example query 'kalvin clein' will match document with 'calvin klein' in title). */
  fuzzy?: boolean | null;
  /** Highlight texts matching the query. Highlighted text will be wrapped with <mark/> tag. Defaults to true if not provided. */
  highlight?: boolean | null;
  /** Searchable fields to search in. If not provided, search is executed on all searchable fields in schemas */
  searchFields?: string[];
  /** Document types to search in. If not provided, search is executed on all document types enabled for the site */
  documentTypes?: string[];
  /** Include seo hidden documents. Defaults to false if not provided. */
  includeSeoHidden?: boolean | null;
  /**
   * The properties/overrides/experiments to enable for this request. Currently supported: `scoring_profile`, `result-format`.
   * @maxSize 10
   */
  properties?: SearchProperty[];
}

export interface FederatedSearchResponse {
  /** Search results from multiple indexes. */
  results?: FederatedSearchDocuments[];
}

export interface FederatedSearchDocuments {
  /** Document type of documents */
  documentType?: string | null;
  /** Documents of document type */
  documents?: Record<string, any>[] | null;
  /** Total count of matching documents for document type */
  total?: number;
}

export interface SuggestRequest {
  /**
   * Text to search for. Fields configured in suggester configuration will be searched.
   * @maxLength 50
   */
  query?: string | null;
  /**
   * Document type of documents to search for. All document types are searched if not provided.
   * @minLength 2
   */
  documentType?: string | null;
  /** Fields to order by. */
  ordering?: OrderingClauses;
  /**
   * Number of suggested document to return.
   * @min 1
   * @max 100
   */
  limit?: number;
  /**
   * Language to search in.
   * @minLength 2
   */
  language?: string | null;
  /** Filter in platformized query language (for example {'field': {'$eq': 'value'}}) */
  filter?: Record<string, any> | null;
  /** Searchable fields to search in. If not provided, search is executed on all suggestable fields in schema */
  searchFields?: string[];
  /** A list of fields to include in the result set. If not provided, all fields of schema will be included. */
  fields?: string[];
  /** Include seo hidden documents. Defaults to false if not provided. */
  includeSeoHidden?: boolean | null;
  /**
   * The properties/overrides/experiments to enable for this request. Currently supported: `scoring_profile`, `result-format`.
   * @maxSize 10
   */
  properties?: SearchProperty[];
}

export interface SuggestResponse {
  /** Suggested documents. */
  documents?: Record<string, any>[] | null;
}

export interface FederatedSuggestRequest {
  /**
   * Text to search for. Fields configured in suggester configuration will be searched.
   * @maxLength 50
   */
  query?: string | null;
  /**
   * Language to search in.
   * @minLength 2
   */
  language?: string | null;
  /**
   * Number of suggested document to return per document type.
   * @min 1
   * @max 100
   */
  limit?: number;
  /** Searchable fields to search in. If not provided, search is executed on all suggestable fields in schemas */
  searchFields?: string[];
  /** Document types to search in. If not provided, search is executed on all document types enabled for the site */
  documentTypes?: string[];
  /** Include seo hidden documents. Defaults to false if not provided. */
  includeSeoHidden?: boolean | null;
  /**
   * The properties/overrides/experiments to enable for this request. Currently supported: `scoring_profile`, `result-format`.
   * @maxSize 10
   */
  properties?: SearchProperty[];
}

export interface FederatedSuggestResponse {
  /** Suggest results from multiple indexes. */
  results?: FederatedSuggestDocuments[];
}

export interface FederatedSuggestDocuments {
  /** Document type of documents */
  documentType?: string | null;
  /** Documents of document type */
  documents?: Record<string, any>[] | null;
}

export interface RelatedRequest {
  /**
   * ID of the document to fetch related documents for.
   * @minLength 1
   */
  documentId?: string | null;
  /**
   * Document type of the document.
   * @minLength 2
   */
  documentType?: string | null;
  /** Fields to order by. */
  ordering?: OrderingClauses;
  /**
   * Language to search in.
   * @minLength 2
   */
  language?: string | null;
  /** Filter in platformized query language (for example {'field': {'$eq': 'value'}}). */
  filter?: Record<string, any> | null;
  /** Searchable fields to compare documents by. If not provided, all searchable fields in schema are used */
  searchFields?: string[];
  /** A list of fields to include in the result set. If not provided, all fields of schema will be included. */
  fields?: string[];
  /** Number of related documents to return */
  limit?: number;
  /**
   * The properties/overrides/experiments to enable for this request. Currently supported: `scoring_profile`.
   * @maxSize 10
   */
  properties?: SearchProperty[];
  /** Include seo hidden documents. Defaults to false if not provided. */
  includeSeoHidden?: boolean | null;
}

export interface RelatedResponse {
  /** Documents matching filter and query. */
  documents?: Record<string, any>[] | null;
}

export interface AutocompleteRequest {
  /**
   * Query phrase to fetch completions for.
   * @maxLength 50
   */
  query?: string | null;
  /**
   * Document type to use to search for phrases.
   * @minLength 2
   */
  documentType?: string | null;
  /**
   * Limit of phrases to fetch.
   * @min 1
   * @max 100
   */
  limit?: number;
  /**
   * Language to search in.
   * @minLength 2
   */
  language?: string | null;
  /** Filter in platfromized query language (for example {'field': {'$eq': 'value'}}) */
  filter?: Record<string, any> | null;
  /** Searchable fields to use for query completion. If not provided, all searchable fields in schema are used */
  searchFields?: string[];
  /** Include seo hidden documents. Defaults to false if not provided. */
  includeSeoHidden?: boolean | null;
}

export interface AutocompleteResponse {
  /** Suggested phrases. */
  values?: AutocompleteResponseValue[];
}

export interface AutocompleteResponseValue {
  /** Suggested phrase. */
  query?: string;
}

export interface FederatedAutocompleteRequest {
  /**
   * Query phrase to fetch completions for.
   * @maxLength 50
   */
  query?: string | null;
  /**
   * Language to search in.
   * @minLength 2
   */
  language?: string | null;
  /**
   * Number of queries to return per document type.
   * @min 1
   * @max 100
   */
  limit?: number;
  /** Searchable fields to search in. If not provided, search is executed on all autocompletable fields in schemas */
  searchFields?: string[];
  /** Document types to autocomplete in. If not provided, autocomplete is executed on all document types enabled for the site */
  documentTypes?: string[];
  /** Include seo hidden documents. Defaults to false if not provided. */
  includeSeoHidden?: boolean | null;
}

export interface FederatedAutocompleteResponse {
  /** Suggested phrases from multiple indexes */
  results?: FederatedAutocompleteResults[];
}

export interface FederatedAutocompleteResults {
  /** Document type of queries */
  documentType?: string | null;
  /** Suggested phrases */
  values?: AutocompleteResponseValue[];
}

export interface TrendingRequest {
  documentTypes?: string[];
  /** @minLength 2 */
  language?: string | null;
  /** Include seo hidden documents. Defaults to false if not provided. */
  includeSeoHidden?: boolean | null;
  /**
   * The properties/overrides/experiments to enable for this request. Currently supported: `scoring_profile`, `result-format`.
   * @maxSize 10
   */
  properties?: SearchProperty[];
}

export interface TrendingResponse {
  results?: TrendingItems[];
}

export interface TrendingItems {
  documentType?: string;
  documents?: Record<string, any>[] | null;
}
